import React from 'react';
import './PlansPricingManager.css';
import {
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  IconButton,
  Switch,
  TextField,
  Tooltip,
} from '@material-ui/core';
import CheckIcon from '@material-ui/icons/Check';
import CloseIcon from '@material-ui/icons/Close';
import HistoryIcon from '@material-ui/icons/History';
import ScheduleIcon from '@material-ui/icons/Schedule';
import TrendingUpIcon from '@material-ui/icons/TrendingUp';
import TrendingDownIcon from '@material-ui/icons/TrendingDown';
import LockIcon from '@material-ui/icons/Lock';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';

/**
 * Plans & Pricing — the rate card behind every price the system quotes.
 *
 * Opened from the Manage Company toolbar, next to Onboarding Requests. What
 * is edited here is the SAME catalog that prices a renewal, a public
 * sign-up, the Create Company wizard and the landing page, so there is no
 * second place for a price to be wrong.
 *
 * ------------------------------------------------------------------
 * The rule the whole screen is built around
 * ------------------------------------------------------------------
 * A customer has already paid for the cycle they are in, so nothing
 * published here touches it:
 *
 *   Money    — settled invoices are receipts and are never rewritten. The
 *              new price lands on the NEXT invoice.
 *   Features — a re-scoped plan is queued against the end of each company's
 *              current cycle and applied there. They keep what they paid
 *              for until that cycle is over.
 *
 * That is why publishing asks two questions rather than just saving: does
 * this reach existing customers at all (or only new sign-ups), and should
 * the feature changes wait for the cycle boundary (they should, unless you
 * are fixing a mistake).
 *
 * Editing is local until Publish. `draft` holds the working copy, the
 * header shows what has changed, and the confirm dialog shows the diff the
 * SERVER computed — the same code that will act on it.
 */

const NAVY = '#0A1640';
const MUTED = '#5B6781';
const FAINT = '#9aa6bd';
const LINE = '#E5EAF4';
const BLUE = '#1E3FD0';
const GOLD = '#C99700';
const GREEN = '#16A34A';
const RED = '#D0342C';

const SWATCHES = [
  '#8B6FC0',
  '#1a3a8f',
  '#B8960C',
  '#D4AF37',
  '#22a06b',
  '#1E3FD0',
  '#0A1640',
  '#D0342C',
];

const TABS = [
  { key: 'plans', label: 'Plans' },
  { key: 'addons', label: 'Add-Ons' },
  { key: 'terms', label: 'Billing Terms' },
  { key: 'rollout', label: 'Rollout' },
  { key: 'history', label: 'History' },
];

const money = (v) => `$${Number(v || 0).toLocaleString('en-US')}`;

const stamp = (ms) => {
  if (!ms) return '';
  try {
    return new Date(ms).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  } catch (e) {
    return '';
  }
};

// Structural comparison of the draft against what is published. Only the
// fields this screen can edit are compared, so an unrelated field the server
// adds later never makes the page look dirty.
const PLAN_KEYS = [
  'name',
  'price',
  'badge',
  'color',
  'active',
  'publicVisible',
];

function planFingerprint(p) {
  const base = PLAN_KEYS.map((k) => `${k}=${p[k] == null ? '' : p[k]}`).join(
    '|'
  );
  return `${base}|features=${(p.features || []).slice().sort().join(',')}`;
}

function addOnFingerprint(a) {
  return ['name', 'price', 'global', 'active']
    .map((k) => `${k}=${a[k] == null ? '' : a[k]}`)
    .join('|');
}

function durationFingerprint(d) {
  return ['label', 'discount', 'badge', 'active']
    .map((k) => `${k}=${d[k] == null ? '' : d[k]}`)
    .join('|');
}

function catalogFingerprint(cat) {
  if (!cat) return '';
  return [
    (cat.plans || []).map(planFingerprint).join('~'),
    (cat.addOns || []).map(addOnFingerprint).join('~'),
    (cat.durations || []).map(durationFingerprint).join('~'),
    `trial=${cat.settings?.trialDays}|${cat.settings?.trialMinWindowDays}`,
  ].join('#');
}

class PlansPricingManager extends React.Component {
  state = {
    tab: 'plans',
    draft: null,
    // Which published version the draft was seeded from — reseeding on a
    // newer one is how a publish (or another admin's) lands on screen
    // without wiping edits in progress.
    draftVersion: null,
    selectedPlanId: null,
    publishOpen: false,
    preview: null,
    previewing: false,
    publishing: false,
    applyToExisting: true,
    applyFeaturesNow: false,
    changeNote: '',
    customRateDraft: {},
  };

  componentDidMount() {
    this.props.onLoad({});
    this.seedDraft();
  }

  componentDidUpdate(prevProps) {
    const before = prevProps.overview?.catalog?.version;
    const after = this.props.overview?.catalog?.version;
    if (before !== after) this.seedDraft();
  }

  seedDraft = () => {
    const cat = this.props.overview?.catalog;
    if (!cat) return;
    this.setState({
      draft: JSON.parse(JSON.stringify(cat)),
      draftVersion: cat.version,
      selectedPlanId:
        this.state.selectedPlanId ||
        (cat.plans && cat.plans.length ? cat.plans[0].id : null),
    });
  };

  // ---- draft helpers -------------------------------------------------

  isDirty = () =>
    !!this.state.draft &&
    catalogFingerprint(this.state.draft) !==
      catalogFingerprint(this.props.overview?.catalog);

  patchPlan = (planId, patch) =>
    this.setState((s) => ({
      draft: {
        ...s.draft,
        plans: s.draft.plans.map((p) =>
          p.id === planId ? { ...p, ...patch } : p
        ),
      },
    }));

  toggleFeature = (planId, featureId) =>
    this.setState((s) => ({
      draft: {
        ...s.draft,
        plans: s.draft.plans.map((p) => {
          if (p.id !== planId) return p;
          const has = (p.features || []).includes(featureId);
          return {
            ...p,
            features: has
              ? p.features.filter((f) => f !== featureId)
              : [...(p.features || []), featureId],
          };
        }),
      },
    }));

  setCategory = (planId, category, on) =>
    this.setState((s) => {
      const ids = (this.props.overview?.features || [])
        .filter((f) => f.category === category)
        .map((f) => f.id);
      return {
        draft: {
          ...s.draft,
          plans: s.draft.plans.map((p) => {
            if (p.id !== planId) return p;
            const rest = (p.features || []).filter((f) => !ids.includes(f));
            return { ...p, features: on ? [...rest, ...ids] : rest };
          }),
        },
      };
    });

  patchAddOn = (addOnId, patch) =>
    this.setState((s) => ({
      draft: {
        ...s.draft,
        addOns: s.draft.addOns.map((a) =>
          a.id === addOnId ? { ...a, ...patch } : a
        ),
      },
    }));

  patchDuration = (durationId, patch) =>
    this.setState((s) => ({
      draft: {
        ...s.draft,
        durations: s.draft.durations.map((d) =>
          d.id === durationId ? { ...d, ...patch } : d
        ),
      },
    }));

  patchSettings = (patch) =>
    this.setState((s) => ({
      draft: { ...s.draft, settings: { ...s.draft.settings, ...patch } },
    }));

  discard = () => this.seedDraft();

  // ---- publish -------------------------------------------------------

  openPublish = () => {
    this.setState({ publishOpen: true, previewing: true, preview: null });
    this.props.onPreview(this.payload(), (ok, data) =>
      this.setState({ previewing: false, preview: ok ? data : null })
    );
  };

  payload = () => {
    const d = this.state.draft || {};
    return {
      plans: d.plans,
      addOns: d.addOns,
      durations: d.durations,
      settings: d.settings,
    };
  };

  publish = () => {
    this.setState({ publishing: true });
    this.props.onPublish(
      {
        ...this.payload(),
        applyToExisting: this.state.applyToExisting,
        applyFeaturesNow: this.state.applyFeaturesNow,
        changeNote: this.state.changeNote,
      },
      (ok) =>
        this.setState({
          publishing: false,
          publishOpen: !ok,
          changeNote: ok ? '' : this.state.changeNote,
          preview: null,
        })
    );
  };

  // ---- render --------------------------------------------------------

  render() {
    const { overview, loading } = this.props;
    const { draft, tab } = this.state;

    if (!draft) {
      return (
        <div
          style={{ padding: '64px 24px', textAlign: 'center', color: MUTED }}
        >
          {loading ? 'Loading the rate card…' : 'No rate card available.'}
        </div>
      );
    }

    const dirty = this.isDirty();

    return (
      <div className="pp-root">
        {this.renderHeader(dirty)}
        {this.renderTabs()}
        {tab === 'plans' && this.renderPlans()}
        {tab === 'addons' && this.renderAddOns()}
        {tab === 'terms' && this.renderTerms()}
        {tab === 'rollout' && this.renderRollout()}
        {tab === 'history' && this.renderHistory()}
        {this.renderPublishDialog()}
        {dirty && this.renderStickyBar()}
        {overview ? null : null}
      </div>
    );
  }

  renderHeader(dirty) {
    const { overview } = this.props;
    const cat = overview?.catalog || {};
    return (
      <div className="pp-header">
        <div>
          <div style={{ fontSize: 21, fontWeight: 800, color: NAVY }}>
            Plans &amp; Pricing
          </div>
          <div style={{ fontSize: 13, color: MUTED, marginTop: 4 }}>
            Rate card v{cat.version}
            {cat.publishedAt ? ` · published ${stamp(cat.publishedAt)}` : ''}
            {cat.publishedByName ? ` by ${cat.publishedByName}` : ''}
            {' · '}
            {overview?.totalCompanies || 0} companies billed from it
          </div>
        </div>
        <div className="pp-header-actions">
          {dirty && (
            <Button
              variant="text"
              onClick={this.discard}
              style={{ textTransform: 'none', fontWeight: 700, color: MUTED }}
            >
              Discard changes
            </Button>
          )}
          <Button
            variant="contained"
            disabled={!dirty}
            onClick={this.openPublish}
            style={{
              textTransform: 'none',
              fontWeight: 700,
              background: dirty ? GOLD : '#e7eaf2',
              color: dirty ? '#fff' : FAINT,
              boxShadow: 'none',
              padding: '8px 18px',
            }}
          >
            Review &amp; Publish
          </Button>
        </div>
      </div>
    );
  }

  renderStickyBar() {
    return (
      <div
        className="pp-sticky"
        style={{ fontSize: 13.5, color: '#7A5B00', fontWeight: 600 }}
      >
        <InfoOutlinedIcon style={{ fontSize: 19 }} />
        <span>
          Unpublished changes. Nothing is billed or granted until you publish.
        </span>
        <span className="pp-row-grow" style={{ flex: 1 }} />
        <Button
          variant="contained"
          onClick={this.openPublish}
          style={{
            textTransform: 'none',
            fontWeight: 700,
            background: GOLD,
            color: '#fff',
            boxShadow: 'none',
          }}
        >
          Review &amp; Publish
        </Button>
      </div>
    );
  }

  renderTabs() {
    const { overview } = this.props;
    const counts = {
      rollout: (overview?.queuedCount || 0) + (overview?.customRateCount || 0),
      history: (overview?.history || []).length,
    };
    return (
      <div className="pp-tabs">
        {TABS.map((t) => {
          const active = this.state.tab === t.key;
          return (
            <button
              key={t.key}
              type="button"
              className="pp-tab"
              onClick={() => this.setState({ tab: t.key })}
              style={{
                color: active ? NAVY : MUTED,
                borderBottomColor: active ? BLUE : 'transparent',
              }}
            >
              {t.label}
              {counts[t.key] ? (
                <span
                  style={{
                    marginLeft: 8,
                    background: active ? BLUE : '#EEF1F7',
                    color: active ? '#fff' : MUTED,
                    borderRadius: 999,
                    fontSize: 11.5,
                    fontWeight: 700,
                    padding: '2px 8px',
                  }}
                >
                  {counts[t.key]}
                </span>
              ) : null}
            </button>
          );
        })}
      </div>
    );
  }

  // ---- Plans ---------------------------------------------------------

  renderPlans() {
    const { draft, selectedPlanId } = this.state;
    const stats = this.props.overview?.planStats || {};
    const selected =
      draft.plans.find((p) => p.id === selectedPlanId) || draft.plans[0];

    return (
      <>
        <Explainer>
          A price change reaches existing companies on their <b>next</b> billing
          cycle — the cycle they have already paid for is never re-priced.
          Changing what a plan <i>includes</i> waits for that same boundary, so
          nobody loses a feature they are mid-cycle on.
        </Explainer>

        <div className="pp-plan-grid">
          {draft.plans.map((p) => this.renderPlanCard(p, stats[p.id]))}
        </div>

        {selected && this.renderPlanEditor(selected, stats[selected.id])}
      </>
    );
  }

  renderPlanCard(plan, stat) {
    const selected = plan.id === this.state.selectedPlanId;
    const withdrawn = plan.active === false;
    return (
      <div
        key={plan.id}
        onClick={() => this.setState({ selectedPlanId: plan.id })}
        style={{
          background: '#fff',
          border: `1.5px solid ${selected ? plan.color : LINE}`,
          borderRadius: 14,
          padding: '16px 18px 14px',
          cursor: 'pointer',
          boxShadow: selected
            ? `0 12px 26px -20px ${plan.color}`
            : '0 10px 30px -24px rgba(10,22,64,0.4)',
          opacity: withdrawn ? 0.62 : 1,
          position: 'relative',
        }}
      >
        <div
          style={{
            height: 4,
            width: 42,
            borderRadius: 4,
            background: plan.color,
            marginBottom: 12,
          }}
        />
        <div
          style={{
            display: 'flex',
            alignItems: 'baseline',
            justifyContent: 'space-between',
            gap: 8,
          }}
        >
          <span style={{ fontSize: 17, fontWeight: 800, color: NAVY }}>
            {plan.name}
          </span>
          {plan.badge ? (
            <span
              style={{
                fontSize: 10,
                fontWeight: 800,
                letterSpacing: '0.05em',
                background: `${plan.color}1A`,
                color: plan.color,
                borderRadius: 999,
                padding: '3px 9px',
              }}
            >
              {plan.badge}
            </span>
          ) : null}
        </div>
        <div style={{ margin: '8px 0 2px' }}>
          <span style={{ fontSize: 26, fontWeight: 800, color: NAVY }}>
            {money(plan.price)}
          </span>
          <span style={{ fontSize: 13, color: FAINT, fontWeight: 700 }}>
            {' '}
            / month
          </span>
        </div>
        <div style={{ fontSize: 12.5, color: MUTED, marginTop: 8 }}>
          {(plan.features || []).length} features · {stat ? stat.companies : 0}{' '}
          {stat && stat.companies === 1 ? 'company' : 'companies'}
        </div>
        {stat && stat.companies > 0 ? (
          <div style={{ fontSize: 12, color: FAINT, marginTop: 3 }}>
            {money(stat.monthly)} / month recurring
            {stat.custom ? ` · ${stat.custom} on a custom rate` : ''}
          </div>
        ) : null}
        <div
          style={{ display: 'flex', gap: 6, marginTop: 12, flexWrap: 'wrap' }}
        >
          {plan.lineup === 'alpha' && <MiniTag color={GOLD}>Alpha</MiniTag>}
          {withdrawn && <MiniTag color={RED}>Not sold</MiniTag>}
          {plan.publicVisible === false && plan.lineup !== 'alpha' && (
            <MiniTag color={MUTED}>Hidden</MiniTag>
          )}
        </div>
      </div>
    );
  }

  renderPlanEditor(plan, stat) {
    const { overview } = this.props;
    const features = overview?.features || [];
    const categories = overview?.categories || [];
    const owned = new Set(plan.features || []);
    const published = (overview?.catalog?.plans || []).find(
      (p) => p.id === plan.id
    );
    const priceMoved =
      published && Number(published.price) !== Number(plan.price);

    // An add-on whose every capability the plan already covers is bundled —
    // shown here because it silently stops being billable on top, which is
    // the sort of thing you want to see BEFORE you publish.
    const bundled = (this.state.draft.addOns || []).filter((a) => {
      if (plan.bundlesAddOns === false) return false;
      const af = a.features || [];
      return af.length > 0 && af.every((f) => owned.has(f));
    });

    return (
      <div
        style={{
          background: '#fff',
          border: `1px solid ${LINE}`,
          borderLeft: `4px solid ${plan.color}`,
          borderRadius: 14,
          padding: '22px 24px',
          boxShadow: '0 10px 30px -24px rgba(10,22,64,0.45)',
        }}
      >
        <SectionLabel>Editing {plan.name}</SectionLabel>

        <div className="pp-field-grid">
          <TextField
            size="small"
            variant="outlined"
            label="Plan name"
            value={plan.name || ''}
            onChange={(e) => {
              const value = e.target.value;
              this.patchPlan(plan.id, { name: value });
            }}
            helperText={
              published && published.name !== plan.name
                ? `Companies on "${published.name}" move with it`
                : ' '
            }
          />
          <TextField
            size="small"
            variant="outlined"
            label="Price per month"
            value={plan.price == null ? '' : plan.price}
            onChange={(e) => {
              const value = e.target.value.replace(/[^0-9]/g, '');
              this.patchPlan(plan.id, {
                price: value === '' ? '' : Number(value),
              });
            }}
            InputProps={{
              startAdornment: (
                <span style={{ color: FAINT, marginRight: 4 }}>$</span>
              ),
            }}
            helperText={
              priceMoved
                ? `${money(published.price)} → ${money(
                    plan.price
                  )} from the next cycle`
                : ' '
            }
            FormHelperTextProps={
              priceMoved
                ? { style: { color: GOLD, fontWeight: 700 } }
                : undefined
            }
          />
          <TextField
            size="small"
            variant="outlined"
            label="Badge (optional)"
            placeholder="POPULAR"
            value={plan.badge || ''}
            onChange={(e) => {
              const value = e.target.value;
              this.patchPlan(plan.id, { badge: value || null });
            }}
            helperText=" "
          />
        </div>

        <div
          style={{
            display: 'flex',
            gap: 26,
            flexWrap: 'wrap',
            alignItems: 'center',
            margin: '4px 0 18px',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: 12.5, fontWeight: 700, color: MUTED }}>
              Accent
            </span>
            {SWATCHES.map((c) => (
              <button
                key={c}
                type="button"
                aria-label={`Accent ${c}`}
                onClick={() => this.patchPlan(plan.id, { color: c })}
                style={{
                  width: 20,
                  height: 20,
                  borderRadius: '50%',
                  background: c,
                  cursor: 'pointer',
                  border:
                    plan.color === c ? `2px solid ${NAVY}` : '2px solid #fff',
                  boxShadow: '0 0 0 1px #dfe4ee',
                }}
              />
            ))}
          </div>
          <ToggleRow
            label="Sold to new companies"
            hint="Off keeps every current company on it, but nobody new can buy it"
            checked={plan.active !== false}
            onChange={(v) => this.patchPlan(plan.id, { active: v })}
          />
          <ToggleRow
            label="Show on the public site"
            hint="Controls the landing page and the sign-up wizard only"
            checked={plan.publicVisible !== false}
            onChange={(v) => this.patchPlan(plan.id, { publicVisible: v })}
          />
        </div>

        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
            flexWrap: 'wrap',
            borderTop: `1px solid ${LINE}`,
            paddingTop: 18,
          }}
        >
          <SectionLabel>What this plan includes</SectionLabel>
          <span style={{ fontSize: 12.5, color: FAINT }}>
            {owned.size} of {features.length} features
            {stat && stat.companies
              ? ` · ${stat.companies} ${
                  stat.companies === 1 ? 'company' : 'companies'
                } affected at their next cycle`
              : ''}
          </span>
        </div>

        <div className="pp-feature-grid">
          {categories.map((category) => {
            const rows = features.filter((f) => f.category === category);
            if (rows.length === 0) return null;
            const all = rows.every((f) => owned.has(f.id));
            return (
              <div
                key={category}
                style={{
                  border: `1px solid ${LINE}`,
                  borderRadius: 12,
                  padding: '12px 14px',
                  background: '#FCFDFF',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginBottom: 6,
                  }}
                >
                  <span
                    style={{
                      fontSize: 12,
                      fontWeight: 800,
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                      color: plan.color,
                    }}
                  >
                    {category}
                  </span>
                  <button
                    type="button"
                    onClick={() => this.setCategory(plan.id, category, !all)}
                    style={{
                      border: 'none',
                      background: 'transparent',
                      color: BLUE,
                      fontWeight: 700,
                      fontSize: 11.5,
                      cursor: 'pointer',
                      padding: 0,
                    }}
                  >
                    {all ? 'Clear' : 'Select all'}
                  </button>
                </div>
                {rows.map((f) => (
                  <label
                    key={f.id}
                    style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 4,
                      cursor: 'pointer',
                      padding: '1px 0',
                    }}
                  >
                    <Checkbox
                      size="small"
                      color="primary"
                      checked={owned.has(f.id)}
                      onChange={() => this.toggleFeature(plan.id, f.id)}
                      style={{ padding: 4 }}
                    />
                    <span style={{ fontSize: 13.5, color: NAVY, marginTop: 5 }}>
                      {f.label}
                    </span>
                  </label>
                ))}
              </div>
            );
          })}
        </div>

        <div
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 10,
            marginTop: 18,
            background: '#F4F6FB',
            border: `1px solid ${LINE}`,
            borderRadius: 11,
            padding: '11px 14px',
            fontSize: 12.5,
            color: MUTED,
            lineHeight: 1.55,
          }}
        >
          <LockIcon style={{ fontSize: 17, color: FAINT, marginTop: 1 }} />
          <div>
            <b style={{ color: NAVY }}>Included in every plan:</b> Settings,
            Rules, Machines, Games, Assign Machine, Manage Bonus, Manage Users
            and Maintenance Permit. These are how a company runs itself and how
            it lets an NDS technician in — they are not sold, so they cannot be
            switched off.
            {bundled.length > 0 && (
              <div style={{ marginTop: 6 }}>
                <b style={{ color: NAVY }}>Bundled add-ons:</b>{' '}
                {bundled.map((a) => a.name).join(', ')} — already covered by
                this plan, so they are never billed on top of it.
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ---- Add-ons -------------------------------------------------------

  renderAddOns() {
    const { draft } = this.state;
    const counts = this.props.overview?.addOnCounts || {};
    const featureById = (this.props.overview?.features || []).reduce(
      (acc, f) => {
        acc[f.id] = f;
        return acc;
      },
      {}
    );
    const published = this.props.overview?.catalog?.addOns || [];

    return (
      <>
        <Explainer>
          Add-ons are billed on top of the plan, every month, at the price set
          here. A change follows the same rule as a plan price: it lands on the
          next invoice, never on a cycle already paid.
        </Explainer>

        <div style={{ display: 'grid', gap: 14 }}>
          {draft.addOns.map((a) => {
            const before = published.find((x) => x.id === a.id);
            const moved = before && Number(before.price) !== Number(a.price);
            return (
              <div
                key={a.id}
                className="pp-addon-row"
                style={{ opacity: a.active === false ? 0.62 : 1 }}
              >
                <div>
                  <TextField
                    size="small"
                    variant="outlined"
                    label="Add-on name"
                    fullWidth
                    value={a.name || ''}
                    onChange={(e) => {
                      const value = e.target.value;
                      this.patchAddOn(a.id, { name: value });
                    }}
                  />
                  <div style={{ fontSize: 11.5, color: FAINT, marginTop: 6 }}>
                    Grants{' '}
                    {(a.features || [])
                      .map((f) => featureById[f]?.label || f)
                      .join(', ') || '—'}
                  </div>
                </div>
                <TextField
                  size="small"
                  variant="outlined"
                  label="Per month"
                  value={a.price == null ? '' : a.price}
                  onChange={(e) => {
                    const value = e.target.value.replace(/[^0-9]/g, '');
                    this.patchAddOn(a.id, {
                      price: value === '' ? '' : Number(value),
                    });
                  }}
                  InputProps={{
                    startAdornment: (
                      <span style={{ color: FAINT, marginRight: 4 }}>$</span>
                    ),
                  }}
                  helperText={
                    moved
                      ? `${money(before.price)} → ${money(a.price)}`
                      : `${counts[a.id] || 0} ${
                          (counts[a.id] || 0) === 1 ? 'company' : 'companies'
                        }`
                  }
                  FormHelperTextProps={
                    moved
                      ? { style: { color: GOLD, fontWeight: 700 } }
                      : undefined
                  }
                />
                <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
                  <ToggleRow
                    label="Sold to Global plans"
                    hint="Off makes it an Alpha-only module"
                    checked={a.global !== false}
                    onChange={(v) => this.patchAddOn(a.id, { global: v })}
                  />
                  <ToggleRow
                    label="On sale"
                    hint="Off hides it from the pickers; companies that own it keep it"
                    checked={a.active !== false}
                    onChange={(v) => this.patchAddOn(a.id, { active: v })}
                  />
                </div>
                <div
                  style={{
                    fontSize: 12.5,
                    color: MUTED,
                    fontWeight: 700,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {counts[a.id] || 0} sold
                </div>
              </div>
            );
          })}
        </div>
      </>
    );
  }

  // ---- Billing terms -------------------------------------------------

  renderTerms() {
    const { draft } = this.state;
    return (
      <>
        <Explainer>
          Longer terms are sold at a discount off the monthly rate. Alpha
          companies are billed flat — the discounts below apply to Global plans
          only. The month lengths themselves are fixed: every license and every
          payment row is stored against them.
        </Explainer>

        <div style={{ display: 'grid', gap: 12, marginBottom: 30 }}>
          {draft.durations.map((d) => {
            const pct = Math.round((Number(d.discount) || 0) * 100);
            const monthly = this.headlinePrice();
            const term = monthly * d.months;
            const net = term - Math.round((term * pct) / 100);
            return (
              <div
                key={d.id}
                className="pp-term-row"
                style={{ opacity: d.active === false ? 0.62 : 1 }}
              >
                <div style={{ fontSize: 15, fontWeight: 800, color: NAVY }}>
                  {d.label}
                </div>
                <TextField
                  size="small"
                  variant="outlined"
                  label="Discount"
                  value={pct}
                  disabled={d.months === 1}
                  onChange={(e) => {
                    const value = e.target.value.replace(/[^0-9]/g, '');
                    this.patchDuration(d.id, {
                      discount: (Number(value) || 0) / 100,
                    });
                  }}
                  InputProps={{
                    endAdornment: <span style={{ color: FAINT }}>%</span>,
                  }}
                />
                <TextField
                  size="small"
                  variant="outlined"
                  label="Badge"
                  value={d.badge || ''}
                  onChange={(e) => {
                    const value = e.target.value;
                    this.patchDuration(d.id, { badge: value || null });
                  }}
                />
                <div style={{ fontSize: 12.5, color: MUTED }}>
                  {money(monthly)}/mo becomes{' '}
                  <b style={{ color: NAVY }}>{money(net)}</b> for {d.months}{' '}
                  {d.months === 1 ? 'month' : 'months'}
                  {pct > 0 ? ` (saves ${money(term - net)})` : ''}
                </div>
                <ToggleRow
                  label="Offered"
                  hint={
                    d.months === 1
                      ? 'The 1-month term is the fallback cycle and stays available'
                      : 'Off removes it from the term pickers'
                  }
                  checked={d.active !== false}
                  disabled={d.months === 1}
                  onChange={(v) => this.patchDuration(d.id, { active: v })}
                />
              </div>
            );
          })}
        </div>

        <SectionLabel>Free trial</SectionLabel>
        <div className="pp-trial-grid">
          <TextField
            size="small"
            variant="outlined"
            label="Trial entitlement (days)"
            value={draft.settings?.trialDays ?? ''}
            onChange={(e) => {
              const value = e.target.value.replace(/[^0-9]/g, '');
              this.patchSettings({
                trialDays: value === '' ? '' : Number(value),
              });
            }}
          />
          <TextField
            size="small"
            variant="outlined"
            label="Minimum trial window (days)"
            value={draft.settings?.trialMinWindowDays ?? ''}
            onChange={(e) => {
              const value = e.target.value.replace(/[^0-9]/g, '');
              this.patchSettings({
                trialMinWindowDays: value === '' ? '' : Number(value),
              });
            }}
          />
          <div
            style={{
              gridColumn: '1 / -1',
              fontSize: 12.5,
              color: MUTED,
              lineHeight: 1.6,
            }}
          >
            Billing always lands on the 15th, so a trial ends on the next one
            and almost never comes to exactly the entitlement. The difference is
            settled in dollars on the first invoice — short trials get a credit,
            long ones are charged for the extra days — so every new company gets
            the same value whichever day they sign up. A 15th closer than the
            minimum window is skipped for the following one: an invoice landing
            days after signup is not a trial.
            <br />
            <b style={{ color: NAVY }}>
              Applies to trials started after you publish.
            </b>{' '}
            A company already inside its trial keeps the dates it was given.
          </div>
        </div>
      </>
    );
  }

  // The plan a term example should quote — the most expensive active Global
  // plan, since that is the one an operator is usually reasoning about.
  headlinePrice() {
    const globals = (this.state.draft?.plans || []).filter(
      (p) => (p.lineup || 'global') === 'global' && p.active !== false
    );
    if (globals.length === 0) return 0;
    return globals.reduce((max, p) => Math.max(max, Number(p.price) || 0), 0);
  }

  // ---- Rollout -------------------------------------------------------

  renderRollout() {
    const { overview } = this.props;
    const pending = overview?.pending || [];
    const grandfathered = overview?.grandfathered || [];

    return (
      <>
        <Explainer>
          Everything waiting to take effect, and everyone deliberately left
          behind. This is where you check that a published change actually
          reached the companies you meant it to.
        </Explainer>

        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
            marginBottom: 10,
            flexWrap: 'wrap',
          }}
        >
          <SectionLabel>
            Queued for the next cycle ({pending.length})
          </SectionLabel>
          {pending.length > 0 && (
            <Button
              variant="outlined"
              size="small"
              onClick={() => this.props.onApplyPending({})}
              style={{ textTransform: 'none', fontWeight: 700 }}
            >
              Apply all now
            </Button>
          )}
        </div>

        {pending.length === 0 ? (
          <EmptyRow
            icon={<ScheduleIcon style={{ fontSize: 28, color: FAINT }} />}
            title="Nothing waiting"
            sub="A plan whose feature list changes queues its companies here until their paid cycle ends."
          />
        ) : (
          <div style={{ display: 'grid', gap: 10, marginBottom: 28 }}>
            {pending.map((c) => (
              <div key={c.companyId} className="pp-row">
                <div>
                  <div style={{ fontWeight: 800, color: NAVY, fontSize: 14.5 }}>
                    {c.name}
                  </div>
                  <div style={{ fontSize: 12.5, color: MUTED }}>
                    {c.planName} · picks up v{c.version} on{' '}
                    {stamp(c.effectiveAt) || 'their next cycle'}
                  </div>
                </div>
                <span className="pp-row-grow" style={{ flex: 1 }} />
                <Button
                  size="small"
                  variant="text"
                  onClick={() =>
                    this.props.onApplyPending({ companyIds: [c.companyId] })
                  }
                  style={{
                    textTransform: 'none',
                    fontWeight: 700,
                    color: BLUE,
                  }}
                >
                  Apply now
                </Button>
              </div>
            ))}
          </div>
        )}

        <SectionLabel>
          Companies on a custom rate ({grandfathered.length})
        </SectionLabel>
        <div style={{ fontSize: 12.5, color: MUTED, margin: '0 0 12px' }}>
          A custom rate outranks the rate card: these companies keep their price
          no matter what is published. Clearing one moves them onto the current
          price at their next cycle.
        </div>

        {grandfathered.length === 0 ? (
          <EmptyRow
            icon={<TrendingUpIcon style={{ fontSize: 28, color: FAINT }} />}
            title="Everyone is on the rate card"
            sub="Publishing a change for new companies only, or entering a negotiated price, lands a company here."
          />
        ) : (
          <div style={{ display: 'grid', gap: 10 }}>
            {grandfathered.map((c) => {
              const draftValue =
                this.state.customRateDraft[c.companyId] != null
                  ? this.state.customRateDraft[c.companyId]
                  : c.rate;
              const diff = Number(c.rate) - Number(c.listRate);
              return (
                <div key={c.companyId} className="pp-row">
                  <div style={{ minWidth: 190 }}>
                    <div
                      style={{ fontWeight: 800, color: NAVY, fontSize: 14.5 }}
                    >
                      {c.name}
                    </div>
                    <div style={{ fontSize: 12.5, color: MUTED }}>
                      {c.planName} · rate card says {money(c.listRate)}
                    </div>
                  </div>
                  <span
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 4,
                      fontSize: 12.5,
                      fontWeight: 800,
                      color: diff < 0 ? GREEN : diff > 0 ? GOLD : MUTED,
                    }}
                  >
                    {diff < 0 ? (
                      <TrendingDownIcon style={{ fontSize: 16 }} />
                    ) : diff > 0 ? (
                      <TrendingUpIcon style={{ fontSize: 16 }} />
                    ) : null}
                    {diff === 0
                      ? 'Same as list'
                      : `${diff < 0 ? '-' : '+'}${money(Math.abs(diff))}`}
                  </span>
                  <span className="pp-row-grow" style={{ flex: 1 }} />
                  <TextField
                    size="small"
                    variant="outlined"
                    label="Custom / month"
                    style={{ width: 130 }}
                    value={draftValue || ''}
                    onChange={(e) => {
                      const value = e.target.value.replace(/[^0-9]/g, '');
                      this.setState((s) => ({
                        customRateDraft: {
                          ...s.customRateDraft,
                          [c.companyId]: value,
                        },
                      }));
                    }}
                  />
                  <Button
                    size="small"
                    variant="outlined"
                    disabled={String(draftValue || '') === String(c.rate || '')}
                    onClick={() =>
                      this.props.onSetCustomRate({
                        companyId: c.companyId,
                        rate: draftValue,
                        note: c.note,
                      })
                    }
                    style={{ textTransform: 'none', fontWeight: 700 }}
                  >
                    Save
                  </Button>
                  <Tooltip title="Move onto the current rate card">
                    <IconButton
                      size="small"
                      onClick={() =>
                        this.props.onSetCustomRate({
                          companyId: c.companyId,
                          rate: '',
                        })
                      }
                    >
                      <CloseIcon style={{ fontSize: 18 }} />
                    </IconButton>
                  </Tooltip>
                </div>
              );
            })}
          </div>
        )}
      </>
    );
  }

  // ---- History -------------------------------------------------------

  renderHistory() {
    const history = this.props.overview?.history || [];
    if (history.length === 0) {
      return (
        <EmptyRow
          icon={<HistoryIcon style={{ fontSize: 28, color: FAINT }} />}
          title="No changes published yet"
          sub="Every publish is recorded here with what changed, who changed it and how many companies it moved."
        />
      );
    }
    return (
      <div style={{ display: 'grid', gap: 14 }}>
        {history.map((h) => (
          <div
            key={h.revisionId}
            style={{
              background: '#fff',
              border: `1px solid ${LINE}`,
              borderRadius: 14,
              padding: '16px 20px',
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'baseline',
                gap: 10,
                flexWrap: 'wrap',
              }}
            >
              <span style={{ fontWeight: 800, color: NAVY, fontSize: 15 }}>
                Version {h.version}
              </span>
              <span style={{ fontSize: 12.5, color: MUTED }}>
                {stamp(h.publishedAt)}
                {h.publishedByName ? ` · ${h.publishedByName}` : ''}
              </span>
              <span style={{ flex: 1 }} />
              <span
                style={{
                  fontSize: 11,
                  fontWeight: 800,
                  borderRadius: 999,
                  padding: '3px 10px',
                  background: h.appliedToExisting ? '#E0EAFF' : '#FFF7DD',
                  color: h.appliedToExisting ? BLUE : '#92670A',
                }}
              >
                {h.appliedToExisting
                  ? 'Applied to existing companies'
                  : 'New companies only'}
              </span>
            </div>
            {h.changeNote ? (
              <div
                style={{
                  fontSize: 13,
                  color: NAVY,
                  fontStyle: 'italic',
                  margin: '8px 0 2px',
                }}
              >
                “{h.changeNote}”
              </div>
            ) : null}
            <ul
              style={{
                margin: '10px 0 0',
                paddingLeft: 18,
                color: MUTED,
                fontSize: 13,
                lineHeight: 1.75,
              }}
            >
              {(h.changes || []).map((c, i) => (
                <li key={i}>{c.label}</li>
              ))}
            </ul>
            <div style={{ fontSize: 12, color: FAINT, marginTop: 10 }}>
              {h.repriced} re-priced · {h.queued} queued for their next cycle ·{' '}
              {h.grandfathered} kept on the old price
            </div>
          </div>
        ))}
      </div>
    );
  }

  // ---- Publish dialog ------------------------------------------------

  renderPublishDialog() {
    const { publishOpen, preview, previewing, publishing } = this.state;
    const changes = preview?.changes || [];
    const priceMoves = !!preview?.priceMoves;
    const featureMoves = changes.some((c) =>
      String(c.type).startsWith('plan-features')
    );

    return (
      <Dialog
        open={publishOpen}
        onClose={() => !publishing && this.setState({ publishOpen: false })}
        maxWidth="sm"
        fullWidth
        className="pp-dialog"
      >
        <DialogContent className="pp-dialog-body">
          <div style={{ fontSize: 19, fontWeight: 800, color: NAVY }}>
            Publish rate card changes
          </div>
          <div style={{ fontSize: 13, color: MUTED, marginTop: 4 }}>
            {previewing
              ? 'Checking what this changes…'
              : `${changes.length} ${
                  changes.length === 1 ? 'change' : 'changes'
                }${
                  preview?.affectedCompanies
                    ? ` · ${preview.affectedCompanies} ${
                        preview.affectedCompanies === 1
                          ? 'company'
                          : 'companies'
                      } affected`
                    : ''
                }`}
          </div>

          {!previewing && changes.length > 0 && (
            <ul
              style={{
                margin: '16px 0 4px',
                paddingLeft: 18,
                color: NAVY,
                fontSize: 13.5,
                lineHeight: 1.85,
                maxHeight: 210,
                overflowY: 'auto',
              }}
            >
              {changes.map((c, i) => (
                <li key={i}>{c.label}</li>
              ))}
            </ul>
          )}

          {!previewing && changes.length === 0 && (
            <div style={{ margin: '18px 0', color: MUTED, fontSize: 13.5 }}>
              Nothing to publish — the draft matches what is already live.
            </div>
          )}

          {priceMoves && (
            <div style={{ marginTop: 16 }}>
              <SectionLabel>Existing companies</SectionLabel>
              <Choice
                selected={this.state.applyToExisting}
                onClick={() => this.setState({ applyToExisting: true })}
                title="Move them onto the new price"
                sub="Takes effect on each company's next billing cycle. Cycles already paid are untouched."
              />
              <Choice
                selected={!this.state.applyToExisting}
                onClick={() => this.setState({ applyToExisting: false })}
                title="New companies only"
                sub="Everyone on an affected plan keeps what they pay today, recorded as a custom rate you can lift later."
              />
            </div>
          )}

          {featureMoves && (
            <label
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 6,
                marginTop: 16,
                cursor: 'pointer',
              }}
            >
              <Checkbox
                size="small"
                color="primary"
                checked={this.state.applyFeaturesNow}
                onChange={(e) =>
                  this.setState({ applyFeaturesNow: e.target.checked })
                }
                style={{ padding: 4 }}
              />
              <span style={{ marginTop: 4 }}>
                <span style={{ fontSize: 13.5, color: NAVY, fontWeight: 700 }}>
                  Apply the feature changes immediately
                </span>
                <span
                  style={{
                    display: 'block',
                    fontSize: 12,
                    color: MUTED,
                    lineHeight: 1.5,
                  }}
                >
                  Off (recommended) waits for each company's cycle to end, so
                  nobody loses a feature they have paid for. Turn it on to fix a
                  mistake.
                </span>
              </span>
            </label>
          )}

          <TextField
            fullWidth
            size="small"
            variant="outlined"
            label="Note (optional)"
            placeholder="Why this changed — shows in the history"
            value={this.state.changeNote}
            onChange={(e) => this.setState({ changeNote: e.target.value })}
            style={{ marginTop: 18 }}
          />

          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 10,
              marginTop: 22,
            }}
          >
            <Button
              variant="text"
              disabled={publishing}
              onClick={() => this.setState({ publishOpen: false })}
              style={{ textTransform: 'none', fontWeight: 700, color: MUTED }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              disabled={publishing || previewing || changes.length === 0}
              onClick={this.publish}
              startIcon={!publishing ? <CheckIcon /> : undefined}
              style={{
                textTransform: 'none',
                fontWeight: 700,
                background:
                  publishing || previewing || changes.length === 0
                    ? '#9aa6bd'
                    : GOLD,
                color: '#fff',
                boxShadow: 'none',
              }}
            >
              {publishing ? 'Publishing…' : 'Publish'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }
}

// ---------------------------------------------------------------------------
// Small presentational pieces
// ---------------------------------------------------------------------------

function SectionLabel({ children }) {
  return (
    <div
      style={{
        fontSize: 12,
        fontWeight: 800,
        textTransform: 'uppercase',
        letterSpacing: '0.06em',
        color: FAINT,
      }}
    >
      {children}
    </div>
  );
}

function MiniTag({ children, color }) {
  return (
    <span
      style={{
        fontSize: 10.5,
        fontWeight: 800,
        letterSpacing: '0.04em',
        textTransform: 'uppercase',
        background: `${color}14`,
        color,
        borderRadius: 999,
        padding: '3px 9px',
      }}
    >
      {children}
    </span>
  );
}

function ToggleRow({ label, hint, checked, onChange, disabled }) {
  return (
    <Tooltip title={hint || ''}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Switch
          size="small"
          color="primary"
          checked={!!checked}
          disabled={disabled}
          onChange={(e) => onChange(e.target.checked)}
        />
        <span
          style={{
            fontSize: 13,
            fontWeight: 700,
            color: disabled ? FAINT : MUTED,
          }}
        >
          {label}
        </span>
      </div>
    </Tooltip>
  );
}

function Explainer({ children }) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 10,
        background: '#F4F6FB',
        border: `1px solid ${LINE}`,
        borderRadius: 12,
        padding: '12px 16px',
        marginBottom: 20,
        fontSize: 13,
        color: MUTED,
        lineHeight: 1.6,
      }}
    >
      <InfoOutlinedIcon style={{ fontSize: 18, color: FAINT, marginTop: 1 }} />
      <div>{children}</div>
    </div>
  );
}

function EmptyRow({ icon, title, sub }) {
  return (
    <div
      style={{
        background: '#fff',
        border: `1px dashed #d6deec`,
        borderRadius: 14,
        padding: '34px 28px',
        textAlign: 'center',
        marginBottom: 24,
      }}
    >
      <div
        style={{
          width: 52,
          height: 52,
          borderRadius: '50%',
          background: '#F4F6FB',
          display: 'grid',
          placeItems: 'center',
          margin: '0 auto',
        }}
      >
        {icon}
      </div>
      <div
        style={{ fontSize: 15.5, fontWeight: 800, color: NAVY, marginTop: 12 }}
      >
        {title}
      </div>
      <div
        style={{
          fontSize: 13,
          color: MUTED,
          marginTop: 5,
          maxWidth: 460,
          margin: '5px auto 0',
          lineHeight: 1.5,
        }}
      >
        {sub}
      </div>
    </div>
  );
}

function Choice({ selected, onClick, title, sub }) {
  return (
    <div
      onClick={onClick}
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 10,
        border: `1.5px solid ${selected ? BLUE : LINE}`,
        background: selected ? '#F5F8FF' : '#fff',
        borderRadius: 11,
        padding: '11px 14px',
        marginTop: 8,
        cursor: 'pointer',
      }}
    >
      <span
        style={{
          width: 16,
          height: 16,
          borderRadius: '50%',
          border: `2px solid ${selected ? BLUE : '#c5cdda'}`,
          display: 'grid',
          placeItems: 'center',
          flexShrink: 0,
          marginTop: 2,
        }}
      >
        {selected && (
          <span
            style={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              background: BLUE,
            }}
          />
        )}
      </span>
      <span>
        <span style={{ fontSize: 13.5, fontWeight: 700, color: NAVY }}>
          {title}
        </span>
        <span
          style={{
            display: 'block',
            fontSize: 12,
            color: MUTED,
            lineHeight: 1.5,
          }}
        >
          {sub}
        </span>
      </span>
    </div>
  );
}

export default PlansPricingManager;
